// Клиент забывания (decommission) сервисного бот-аккаунта:
// `POST /api/v1/connections/forgejo/bot-accounts/{username}/decommission`.
// Контракт — ADR-0065 и docs/spec/modules/repo_host/bot_accounts.md
// § «Забывание бот-аккаунта»: отказы — `ApiError` с кодом (ADR-0091), коды
// перечислены там же. Секрет в ответе не бывает.

import { apiErrorFromResponse } from "../../api/apiError";
import { authedFetch } from "../../auth/authedFetch";
import { buildJsonHeaders } from "./api";

// Что делать с учёткой бота на хосте при забывании. Зеркалит backend
// BotAccountProviderAction. Default LEAVE — учётку провайдера не трогаем
// (забывание только Baton-side); BLOCK/DELETE — по явному выбору клиента.
export type BotAccountProviderAction = "leave" | "block" | "delete";

export interface DecommissionBotRequest {
  readonly provider_action: BotAccountProviderAction;
  // Явный выбор поведения при найденных держателях (ADR-0065 § 6), не
  // подтверждение.
  readonly interrupt_holders: boolean;
}

export interface DecommissionBotResponse {
  readonly username: string;
  readonly provider_action: BotAccountProviderAction;
}

export type CredentialHolderMatch =
  | "source"
  | "owner"
  | "owner_unknown"
  | "source_unknown";

export interface CredentialHolder {
  readonly holder_kind: string;
  readonly match: CredentialHolderMatch;
  readonly role: string | null;
  readonly account: string | null;
  readonly count: number;
}

// Эндпоинт забывания живёт не под /tokens, а под общим
// /connections/forgejo (как disconnect), поэтому базу строим отдельно.
const BOT_ACCOUNTS_BASE = "/api/v1/connections/forgejo/bot-accounts";

/**
 * «Забыть» (decommission) сервисный bot-аккаунт: revoke выпущенных
 * Baton'ом токенов + crypto-shred vault-материала + tombstone. При
 * `provider_action: "leave"` учётка на хосте не трогается — забывание
 * только Baton-side.
 *
 * Отказ — `ApiError`: ветвление по паре `status` + `code`.
 */
export async function decommissionBot(
  username: string,
  body: DecommissionBotRequest,
): Promise<DecommissionBotResponse> {
  const response = await authedFetch(
    `${BOT_ACCOUNTS_BASE}/${username}/decommission`,
    {
      method: "POST",
      headers: buildJsonHeaders(),
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "decommissionBot");
  }
  return (await response.json()) as DecommissionBotResponse;
}
